import type { ProtectedApp, UatCrmInspection } from '../api/uatCrmInspection';

export const PROTECTED_CRM_APPS: Record<string, ProtectedApp> = {
  '816d1e20-f38b-466e-96ec-46bb7d0f404c': 'lofty',
  '6e5adb4f-c2b4-459b-8c40-f41bb801bbf2': 'sierra-interactive',
  '048164c9-68a7-4f2c-ac30-3400e3fa47e7': 'boldtrail',
};

export function isCurrentProtectedContext(args: {
  isOpen: boolean; environment: string; isSuperAdmin: boolean;
  selectedOrgId?: string; currentSelectedOrgId?: string; freshSelectedOrgId?: string;
  active?: boolean;
}): boolean {
  return args.isOpen && args.environment === 'uat' && args.isSuperAdmin &&
    Boolean(args.selectedOrgId && PROTECTED_CRM_APPS[args.selectedOrgId]) &&
    args.selectedOrgId === args.currentSelectedOrgId &&
    args.selectedOrgId === args.freshSelectedOrgId && args.active === false;
}

export function isSafeInspection(value: UatCrmInspection, orgId: string, app: ProtectedApp): boolean {
  const metadata = value?.metadata;
  return value.status === 'not_qualified' &&
    value.reason === 'provider_account_field_unreviewed' &&
    value.organization_id === orgId && value.app_key === app &&
    value.read_only === true && value.installed === false && value.writes === 0 &&
    metadata?.connector_found === true &&
    Array.isArray(metadata.plans) && metadata.plans.length <= 20 &&
    metadata.plans.every((plan) =>
      (plan.plan === null || (typeof plan.plan === 'string' && plan.plan.length <= 100)) &&
      ['recognized', 'missing', 'unrecognized'].includes(plan.auth_status)) &&
    Array.isArray(metadata.read_capabilities) && metadata.read_capabilities.length <= 5 &&
    metadata.read_capabilities.every((capability) =>
      ['people', 'users', 'notes', 'tasks', 'transactions'].includes(capability.entity) &&
      typeof capability.list_supported === 'boolean') &&
    Array.isArray(metadata.profile_paths) && metadata.profile_paths.length <= 64 &&
    metadata.profile_paths.every((field) =>
      typeof field.path === 'string' && field.path.length <= 120 &&
      /^[A-Za-z0-9_.$\[\]-]*$/.test(field.path) &&
      ['object', 'array', 'boolean', 'number', 'string', 'null', 'other'].includes(field.type));
}

export function classifyCredentialCallback(args: {
  initiatedGeneration: number; currentGeneration: number;
  initiatedProtectedApp?: string; credentialAppKey: string;
}): 'ignore' | 'inspect_only' | 'ordinary' {
  if (args.initiatedGeneration !== args.currentGeneration) return 'ignore';
  if (!args.initiatedProtectedApp) return 'ordinary';
  return args.credentialAppKey === args.initiatedProtectedApp ? 'inspect_only' : 'ignore';
}

export async function readCurrentInspection(args: {
  orgId: string; app: ProtectedApp; isCurrent: () => boolean;
  request: () => Promise<UatCrmInspection>;
}): Promise<UatCrmInspection | null> {
  if (!args.isCurrent()) return null;
  const result = await args.request();
  if (!args.isCurrent()) return null;
  if (!isSafeInspection(result, args.orgId, args.app)) {
    throw new Error('Inspection unavailable');
  }
  return result;
}
